import { useEffect, useRef } from "react";

import { api } from "../config/variables";
import cobrowseService, { type UserData } from "../services/cobrowseService";
import { readCookie } from "../utils/functions";

/**
 * Custom hook for initializing and managing CobrowseIO service
 *
 * Reads client data from cookie and initializes CobrowseIO
 * when user data (mobile/email) is available.
 * Only enabled in production environment.
 *
 * @returns {void}
 *
 * Side effects:
 * - Initializes CobrowseIO service when user data becomes available (production only)
 * - Updates CobrowseIO custom data when user data changes (production only)
 * - Handles script loading delays with retry mechanism
 * - Polls for client cookie when not available on first load (e.g. after login)
 */
export const useCobrowse = () => {
	const initializedRef = useRef(false);

	const initializeCobrowse = () => {
		if (api.currentEnv !== "production") {
			return;
		}

		const client = readCookie("client", true);
		let userData: UserData = {};

		if (client) {
			userData = {
				mobile: client.contact_no || "",
				email: client.email || "",
			};
		}

		if ((userData.mobile || userData.email) && !initializedRef.current) {
			cobrowseService.init(userData);
			initializedRef.current = true;
		} else if (userData.mobile || userData.email) {
			cobrowseService.updateUserData(userData);
		}
	};

	useEffect(() => {
		if (api.currentEnv !== "production") {
			return;
		}

		initializeCobrowse();

		const pollInterval = setInterval(() => {
			if (initializedRef.current) {
				clearInterval(pollInterval);
				return;
			}
			initializeCobrowse();
		}, 1500);

		const pollTimeout = setTimeout(() => clearInterval(pollInterval), 30000);

		return () => {
			clearInterval(pollInterval);
			clearTimeout(pollTimeout);
		};
	}, []);
};

export default useCobrowse;
